import { useState } from 'react';
import { X, Key, Loader as Loader2 } from 'lucide-react';
import { useBusiness } from '@/contexts/BusinessContext';
import { encryptWiseKey } from '@/lib/encryption';
import { supabase } from '@/lib/supabase';

interface WiseSetupModalProps {
  onClose: () => void;
  onConnected: () => void;
}

export default function WiseSetupModal({ onClose, onConnected }: WiseSetupModalProps) {
  const { selectedBusiness, refreshBusinesses } = useBusiness();
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!apiKey.trim() || !selectedBusiness) return;
    setSaving(true);
    setError('');

    try {
      const encrypted = await encryptWiseKey(apiKey.trim());
      const { error: dbError } = await supabase
        .from('businesses')
        .update({ wise_api_key_encrypted: encrypted })
        .eq('id', selectedBusiness.id);

      if (dbError) throw dbError;
      await refreshBusinesses();
      onConnected();
    } catch (err: any) {
      setError(err.message || 'Failed to save API key');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-4 z-50">
      <div className="glass-modal rounded-2xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center">
              <Key size={18} className="text-teal-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">Connect Wise</h2>
              <p className="text-xs text-white">Enter your Wise API token</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 glass-hover rounded-lg"
          >
            <X size={18} className="text-white" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-white mb-1.5">
              API Token (Read Only)
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter your Wise API token"
              className="w-full px-4 py-2.5 text-sm bg-white/10 border border-white/10 rounded-xl focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200"
            />
          </div>

          <div className="glass-inner rounded-xl p-3">
            <p className="text-[11px] text-white leading-relaxed">
              Your API key is encrypted before storage and only used to fetch balance data through our secure server.
              Go to Wise Settings &gt; API tokens to generate a read-only token.
            </p>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-100 rounded-xl p-3">
              <p className="text-xs text-red-600">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm glass-button text-white rounded-xl font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!apiKey.trim() || saving}
              className="flex-1 px-4 py-2.5 text-sm glass-button-active text-white rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {saving ? 'Connecting...' : 'Connect'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
